import { useTranslation } from 'module/hooks/use-translation';
import { Loading, PageLoading } from 'module/ui/components/atoms/loading';
import { Modal } from 'module/ui/components/atoms/modal';
import { ShimmerText } from 'module/ui/components/atoms/shimmer-text';
import { faMagic } from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowLeft,
  faBolt,
  faInputText,
  faSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate, useParams } from '@rocketlane/rocket-router';
import { Button, Icon, InputField } from '@rocketlane/ui';
import {
  transformApiToPrompt,
  transformPromptToApiUpdate,
} from 'features/documentation-agents/document-agent-transformers';
import {
  DocumentAgentDefineStepContentRemote,
  DocumentAgentTriggerStepContentRemote,
  type DocumentAgentHasAccessToResource,
} from 'fed/document-agent-preview-remote-content';
import { useAccountInitialDataQuery } from 'queries/account';
import { useGetCompanyPageTemplateQuery } from 'queries/company-page-template';
import {
  useGetAdditionalAIContexts,
  useGetDocumentationAgentQuery,
  useUpdateDocumentationAgentMutation,
} from 'queries/document-agent';
import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { TemplateTitle } from 'components/organisms/document/template-title';
import { DocumentEditor } from 'components/organisms/document-editor/document-editor';
import {
  SECTION_MENU_AI_CONTEXTS_META,
  SECTION_MENU_REFRESH_META,
} from 'components/organisms/document-editor/extensions/section-menu/extension';
import { AIContextSidebarItemContent } from './ai-content';
import { DocumentAgentRightPane } from './document-agent-right-pane';
import {
  Label,
  Title as TitleComponent,
} from '../../../organisms/document-editor/extensions/ask-ai/ui/command-processor/section-ui/title';
import type { Editor } from '@tiptap/core';

enum DocumentAgentRightSidebarKeys {
  AIContext = 'ai-context',
  Input = 'input',
  Trigger = 'trigger',
}

function getFirstApiErrorMessage(error: unknown): string | undefined {
  if (error == null) {
    return undefined;
  }

  const responseData = (error as { response?: { data?: unknown } })?.response
    ?.data;

  if (responseData && typeof responseData === 'object') {
    const data = responseData as Record<string, unknown>;
    const errors = data.errors;

    if (Array.isArray(errors) && errors.length > 0) {
      const first = errors[0];

      if (first && typeof first === 'object') {
        const e = first as Record<string, unknown>;
        const fromObject =
          (typeof e.reason === 'string' && e.reason.trim()) ||
          (typeof e.errorMessage === 'string' && e.errorMessage.trim()) ||
          (typeof e.message === 'string' && e.message.trim());

        if (fromObject) {
          return fromObject;
        }
      }

      if (typeof first === 'string' && first.trim()) {
        return first;
      }
    }

    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return undefined;
}

const LayoutContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: var(--spacing-03);
`;

const LeftPane = styled.div`
  width: 65%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
  padding: var(--spacing-06);
  border-radius: var(--border-radius-small);
  background-color: white;
  align-items: center;
  overflow-y: auto;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  height: 32px;

  h1 {
    margin: 0;
  }
`;

const StyledDocumentEditor = styled(DocumentEditor)`
  width: 700px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: var(--spacing-03);
`;

const DocumentAgentPreviewErrorRoot = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 0;
  padding: var(--spacing-09) var(--spacing-08);
  background-color: var(--white-100);
`;

const DocumentAgentPreviewErrorBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 520px;
  padding: var(--spacing-08) var(--spacing-05);
  gap: var(--spacing-04);
`;

const DocumentAgentPreviewErrorTitle = styled.div`
  margin: 0;
  color: var(--text-primary);
  text-align: center;
  font-size: var(--font-size-20);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-28);
`;

const DoneModalFooter = styled.div`
  display: flex;
  gap: var(--spacing-04);
  width: 100%;

  button {
    flex: 1;
    justify-content: center;
    text-align: center;
  }
`;

const PreviewWrapper = styled.div`
  padding: var(--spacing-05) var(--spacing-06);
`;

const LoadingTitle = styled(TitleComponent)`
  margin-top: var(--spacing-03);
  gap: var(--spacing-03);
`;

const TitleWrapper = styled.div`
  width: 700px;
`;

const SidebarLoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: var(--spacing-06);
`;

const StyledTemplateTitle = styled(TemplateTitle)`
  align-self: flex-start;
`;

export const LoaderStreamingComponent = () => {
  return (
    <LoadingTitle>
      <FontAwesomeIcon icon={faMagic} size="sm" />
      <Label>
        <ShimmerText loading={true}>
          <FormattedMessage id="thinking" />
        </ShimmerText>
      </Label>
    </LoadingTitle>
  );
};

const DOCUMENTATION_AGENTS_PATH =
  '/agent-builder/category/documentation-agents';

/** Match /documentation/{id}/preview in pathname (e.g. when host router does not expose :documentationAgentId param). */
const DOCUMENTATION_PREVIEW_PATH_REGEX = /\/documentation\/(\d+)\/preview/;

interface DocumentAgentPreviewPageProps {
  /** When false, hide Done/Save and disable editing in AI context, Input, and Trigger tabs. */
  canEdit?: boolean;
  /** Host RBAC check for federated define-step (e.g. meeting page templates). */
  hasAccessToResource?: DocumentAgentHasAccessToResource;
}

export const DocumentAgentPreviewPage = ({
  canEdit = true,
  hasAccessToResource,
}: DocumentAgentPreviewPageProps = {}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { documentationAgentId: documentationAgentIdParam } = useParams<{
    documentationAgentId: string;
  }>();
  const pathnameMatch = location.pathname.match(
    DOCUMENTATION_PREVIEW_PATH_REGEX,
  );
  const documentationAgentIdFromPathname = pathnameMatch
    ? parseInt(pathnameMatch[1], 10)
    : undefined;
  const leftPaneRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const doneModalPlaceholder = useTranslation(
    'document_agent.preview.done_modal.placeholder',
  );
  const defaultAgentTitle = useTranslation('document_agent.preview.title');
  const loadingMessage = useTranslation('document_agent.preview.loading');
  const [selectedSection, setSelectedSection] = useState<any | null>(null);
  const [isDoneModalOpen, setIsDoneModalOpen] = useState(false);
  const [doneModalTitle, setDoneModalTitle] = useState('');
  const [activeRightSidebarKey, setActiveRightSidebarKey] = useState<
    DocumentAgentRightSidebarKeys | undefined
  >(DocumentAgentRightSidebarKeys.AIContext);
  const [sidebarPrompt, setSidebarPrompt] = useState<any | null>(null);
  const initialSidebarPromptSet = useRef(false);
  const { data: accountData } = useAccountInitialDataQuery();
  const isKnowledgeBaseEnabled =
    accountData?.accountMeta?.features.knowledgeBase;

  const {
    prompt,
    color,
    from,
    documentationAgentId: documentationAgentIdState,
  } = (location.state as {
    prompt: any;
    color: any;
    from?: string;
    documentationAgentId?: number;
  }) || {};
  // Use agent ID from URL when loading directly (e.g. refresh, bookmark); fall back to location.state when navigating from agent builder.
  // Prefer useParams(); when param is null (e.g. host router does not expose :documentationAgentId), parse from pathname.
  const documentationAgentIdFromUrl =
    documentationAgentIdParam != null
      ? parseInt(documentationAgentIdParam, 10)
      : documentationAgentIdFromPathname;
  const documentationAgentId =
    documentationAgentIdFromUrl != null &&
    Number.isInteger(documentationAgentIdFromUrl) &&
    documentationAgentIdFromUrl > 0
      ? documentationAgentIdFromUrl
      : documentationAgentIdState;

  const needTemplateIdFromAgent =
    !prompt?.documentTemplateId && !!documentationAgentId;

  // Fetch agent whenever we have an ID so the Done modal can enable Save and build the update payload (even when prompt.documentTemplateId exists)
  const {
    data: documentationAgent,
    isLoading: isLoadingDocumentationAgent,
    isError: isErrorDocumentationAgent,
    error: documentationAgentQueryError,
  } = useGetDocumentationAgentQuery(documentationAgentId ?? 0, {
    enabled: !!documentationAgentId,
  });

  const templateId =
    prompt?.documentTemplateId ?? documentationAgent?.configuration?.templateId;

  const {
    data: template,
    isLoading: isLoadingCompanyPageTemplate,
    isError: isErrorCompanyPageTemplate,
    error: companyPageTemplateQueryError,
  } = useGetCompanyPageTemplateQuery(templateId ?? 0, {
    enabled: templateId != null && templateId !== '',
  });

  const {
    data: aiContextsData,
    isLoading: isLoadingAIContexts,
    isError: isErrorAIContexts,
    error: aiContextsQueryError,
    refetch: refetchAIContexts,
  } = useGetAdditionalAIContexts(documentationAgentId?.toString() ?? '', {
    enabled: !!documentationAgentId,
  });

  const { mutateAsync: updateDocumentationAgent, isPending: isUpdatingAgent } =
    useUpdateDocumentationAgentMutation({
      onSuccess: () => {
        navigate(DOCUMENTATION_AGENTS_PATH);
      },
    });

  // Reset so we re-run prompt init when agent ID changes (e.g. direct load or navigate to different agent)
  useEffect(() => {
    initialSidebarPromptSet.current = false;
  }, [documentationAgentId]);

  // Set sidebar prompt from fetched documentation agent
  useEffect(() => {
    if (
      documentationAgent &&
      documentationAgent.id === documentationAgentId &&
      !!documentationAgentId &&
      !initialSidebarPromptSet.current
    ) {
      initialSidebarPromptSet.current = true;
      setSidebarPrompt(transformApiToPrompt(documentationAgent));
    }
  }, [documentationAgent, documentationAgentId]);

  // When we have prompt from location.state (e.g. create flow) and no agent ID, use it so Input/Trigger tabs show content
  useEffect(() => {
    if (prompt && !documentationAgentId && !initialSidebarPromptSet.current) {
      initialSidebarPromptSet.current = true;
      setSidebarPrompt(prompt);
    }
  }, [prompt, documentationAgentId]);

  const handleSidebarPromptChange = useCallback((newPrompt: any) => {
    setSidebarPrompt(newPrompt);
  }, []);

  const contextItems = useMemo(() => {
    if (!aiContextsData) {
      return [];
    }

    const result = Object.entries(aiContextsData).map(([id, value]) => ({
      id: id,
      ...value,
    }));

    return result;
  }, [aiContextsData]);

  const handleSectionClick = useCallback((nodeData: any) => {
    setSelectedSection(nodeData);
    setActiveRightSidebarKey(DocumentAgentRightSidebarKeys.AIContext);
  }, []);

  const editorExtensionsConfig = useMemo(() => {
    return {
      sectionMenuConfig: {
        enabled: true,
        documentAgentId: documentationAgentId?.toString(),
        aiContextsData: aiContextsData,
        onSectionClick: handleSectionClick,
      },
    };
  }, [documentationAgentId, aiContextsData, handleSectionClick]);

  const handleEditorInitialize = useCallback((editor: Editor) => {
    editorRef.current = editor;
  }, []);

  // When AI context data updates (e.g. after add/edit in preview), refresh section menu decorations so triggers appear without page reload
  useEffect(() => {
    if (!editorRef.current?.view || aiContextsData == null) {
      return;
    }

    const tr = editorRef.current.state.tr;

    tr.setMeta(SECTION_MENU_REFRESH_META, true);
    tr.setMeta(SECTION_MENU_AI_CONTEXTS_META, aiContextsData);
    editorRef.current.view.dispatch(tr);
  }, [aiContextsData]);

  const rightSidebarItems = useMemo(
    () => [
      {
        key: DocumentAgentRightSidebarKeys.AIContext,
        title: (
          <FormattedMessage
            id="document_agent.preview.ai_context"
            values={{ title: '' }}
          />
        ),
        icon: <Icon icon={faSparkles} size="1x" />,
        content: (
          <AIContextSidebarItemContent
            selectedSection={selectedSection}
            template={template}
            contextItems={contextItems}
            refetchAIContexts={refetchAIContexts}
            documentationAgentId={documentationAgentId}
            readOnly={!canEdit}
          />
        ),
      },
      {
        key: DocumentAgentRightSidebarKeys.Input,
        title: <FormattedMessage id="document_agent.preview.input" />,
        icon: <Icon icon={faInputText} size="1x" />,
        content: sidebarPrompt ? (
          <PreviewWrapper>
            <DocumentAgentDefineStepContentRemote
              prompt={sidebarPrompt}
              onPromptChange={canEdit ? handleSidebarPromptChange : () => {}}
              isTemplateSelectDisabled={true}
              readOnly={!canEdit}
              isKnowledgeBaseEnabled={isKnowledgeBaseEnabled}
              hasAccessToResource={hasAccessToResource}
            />
          </PreviewWrapper>
        ) : (
          <SidebarLoadingWrapper>
            <Loading size="small" description={loadingMessage} />
          </SidebarLoadingWrapper>
        ),
      },
      {
        key: DocumentAgentRightSidebarKeys.Trigger,
        title: <FormattedMessage id="document_agent.preview.trigger" />,
        icon: <Icon icon={faBolt} size="1x" />,
        content: sidebarPrompt ? (
          <PreviewWrapper>
            <DocumentAgentTriggerStepContentRemote
              prompt={sidebarPrompt}
              onPromptChange={canEdit ? handleSidebarPromptChange : () => {}}
              readOnly={!canEdit}
            />
          </PreviewWrapper>
        ) : (
          <SidebarLoadingWrapper>
            <Loading size="small" description={loadingMessage} />
          </SidebarLoadingWrapper>
        ),
      },
    ],
    [
      selectedSection,
      template,
      contextItems,
      refetchAIContexts,
      documentationAgentId,
      sidebarPrompt,
      handleSidebarPromptChange,
      loadingMessage,
      canEdit,
      isKnowledgeBaseEnabled,
      hasAccessToResource,
    ],
  );

  const isLoadingTemplate =
    (needTemplateIdFromAgent && isLoadingDocumentationAgent) ||
    isLoadingCompanyPageTemplate;

  const previewPageErrorMessage = useMemo(() => {
    // Single message: template → AI contexts when agent loaded (first failure wins).
    let failedError: unknown = undefined;

    if (isErrorDocumentationAgent) {
      failedError = documentationAgentQueryError;
    } else if (isErrorCompanyPageTemplate) {
      failedError = companyPageTemplateQueryError;
    } else if (isErrorAIContexts) {
      failedError = aiContextsQueryError;
    }

    return (
      getFirstApiErrorMessage(failedError) ??
      intl.formatMessage({ id: 'document_agent.preview.processing_error' })
    );
  }, [
    isErrorDocumentationAgent,
    documentationAgentQueryError,
    isErrorCompanyPageTemplate,
    companyPageTemplateQueryError,
    isErrorAIContexts,
    aiContextsQueryError,
    intl,
  ]);

  return (
    <ContentContainer>
      {isErrorDocumentationAgent ||
      isErrorCompanyPageTemplate ||
      isErrorAIContexts ? (
        <DocumentAgentPreviewErrorRoot>
          <DocumentAgentPreviewErrorBlock>
            <DocumentAgentPreviewErrorTitle>
              {previewPageErrorMessage}
            </DocumentAgentPreviewErrorTitle>
          </DocumentAgentPreviewErrorBlock>
        </DocumentAgentPreviewErrorRoot>
      ) : isLoadingTemplate || isLoadingAIContexts ? (
        <PageLoading />
      ) : template ? (
        <>
          <Header>
            <Button
              variant="ghost"
              leftIcon={<Icon icon={faArrowLeft} size=".75x" />}
              onClick={() => {
                if (documentationAgentId) {
                  navigate(DOCUMENTATION_AGENTS_PATH);
                } else {
                  navigate(from || '/agent-builder', {
                    state: { prompt, color },
                  });
                }
              }}
            />
            <h1 className="heading-compact-01">
              {documentationAgent?.name ?? prompt?.agentName ?? ''}
            </h1>
            {canEdit ? (
              <div className="flex mr-2">
                <Button
                  variant="primary"
                  disabled={isUpdatingAgent}
                  onClick={async () => {
                    if (!documentationAgent) {
                      return;
                    }

                    const currentName = (
                      documentationAgent.name ??
                      prompt?.agentName ??
                      ''
                    ).trim();
                    const isDefaultTitle =
                      !currentName || currentName === defaultAgentTitle;

                    if (isDefaultTitle) {
                      setDoneModalTitle(currentName || defaultAgentTitle);
                      setIsDoneModalOpen(true);

                      return;
                    }

                    const promptToSave = sidebarPrompt
                      ? { ...sidebarPrompt, agentName: currentName }
                      : null;
                    const payload = promptToSave
                      ? {
                          ...documentationAgent,
                          ...transformPromptToApiUpdate(
                            promptToSave,
                            documentationAgent,
                          ),
                        }
                      : { ...documentationAgent, name: currentName };

                    await updateDocumentationAgent(payload);
                  }}
                >
                  <FormattedMessage id="document_agent.preview.done_button" />
                </Button>
              </div>
            ) : (
              <div></div>
            )}
          </Header>
          <Modal
            open={isDoneModalOpen}
            onCancel={() => {
              setIsDoneModalOpen(false);
              setDoneModalTitle('');
            }}
            title={
              <FormattedMessage id="document_agent.preview.done_modal.title" />
            }
            footer={
              <DoneModalFooter>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsDoneModalOpen(false);
                    setDoneModalTitle('');
                  }}
                >
                  <FormattedMessage id="cancel" />
                </Button>
                <Button
                  variant="primary"
                  disabled={
                    !documentationAgent ||
                    !doneModalTitle.trim() ||
                    isUpdatingAgent
                  }
                  onClick={async () => {
                    if (!documentationAgent || !doneModalTitle.trim()) {
                      return;
                    }

                    const promptToSave = sidebarPrompt
                      ? { ...sidebarPrompt, agentName: doneModalTitle.trim() }
                      : null;
                    const payload = promptToSave
                      ? {
                          ...documentationAgent,
                          ...transformPromptToApiUpdate(
                            promptToSave,
                            documentationAgent,
                          ),
                        }
                      : {
                          ...documentationAgent,
                          name: doneModalTitle.trim(),
                        };

                    await updateDocumentationAgent(payload);
                  }}
                >
                  {isUpdatingAgent ? (
                    <FormattedMessage id="document_agent.preview.done_modal.saving" />
                  ) : (
                    <FormattedMessage id="save" />
                  )}
                </Button>
              </DoneModalFooter>
            }
            width={480}
            destroyOnClose
          >
            <div className="mb-0 mt-3">
              <InputField
                value={doneModalTitle}
                onChange={(e) => setDoneModalTitle(e.target.value)}
                placeholder={doneModalPlaceholder}
                autoFocus
              />
            </div>
          </Modal>
          <LayoutContainer>
            <LeftPane ref={leftPaneRef}>
              <TitleWrapper>
                <StyledTemplateTitle
                  value={template.templateName}
                  onChange={() => {}}
                  readonly={true}
                />
              </TitleWrapper>
              <StyledDocumentEditor
                value={template.tiptapJson}
                readonly={true}
                extensionConfig={editorExtensionsConfig}
                onInitialize={handleEditorInitialize}
              />
            </LeftPane>
            <DocumentAgentRightPane
              items={rightSidebarItems}
              activeKey={activeRightSidebarKey}
              onSelect={(key) =>
                setActiveRightSidebarKey(key as DocumentAgentRightSidebarKeys)
              }
              onClose={() => setActiveRightSidebarKey(undefined)}
              canClose={false}
            />
          </LayoutContainer>
        </>
      ) : (
        <>
          <div>
            <FormattedMessage id="document_agent.preview.no_template" />
          </div>
        </>
      )}
    </ContentContainer>
  );
};
